import React, { FunctionComponent, ReactNode, useContext } from 'react'
import { Accordion, Heading, useIsTheme, Theme, Text, GridWall, useThemePerClass } from '@afterpaytouch/core'
import { useInlineArcadeComponentsEnabled, useIsDisabledUpdatingCheckout, useIsShippingOptionAddressError, useShippingOption } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { ShippingOptionContext } from './context'
import { ExpandableComponent, ShippingOption, ShippingOptionStatus } from '../../model'
import { ModuleContainer } from '../ModuleContainer'
import { useSummaryHeaderLabel, useSummarySubtextLabel } from './hooks'
import { useCheckoutColor } from '../../utils/color'

interface Props {
  children: ReactNode
  selectedShippingOption?: ShippingOption
  hasAllowedShippingOptions: boolean
}

export const ShippingOptionWrapper: FunctionComponent<Props> = ({ children, selectedShippingOption, hasAllowedShippingOptions }) => {
  const themedClass = useThemePerClass(styles)
  const { shippingOptionsStatus } = useShippingOption()
  const isShippingAddressError = useIsShippingOptionAddressError()
  const isDisabled = useIsDisabledUpdatingCheckout()
  const { headingTextColor, subtextColor } = useCheckoutColor({ isDisabled: isDisabled || isShippingAddressError })
  const isCashTheme = useIsTheme(Theme.Cash)
  const { isExpanded, handleExpandedStateChange } = useContext(ShippingOptionContext)
  const inlineArcadeComponentsEnabled = useInlineArcadeComponentsEnabled()
  const { smallTextSize, textSize, sectionHeadingSize } = useCheckoutSizes()
  const summaryHeadingLabel = useSummaryHeaderLabel(hasAllowedShippingOptions, selectedShippingOption)
  const summarySubtextLabel = useSummarySubtextLabel(selectedShippingOption)

  const selectedShippingOptionName = selectedShippingOption?.name

  const renderSummaryHeader = (): JSX.Element => {
    if (inlineArcadeComponentsEnabled) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Text color={headingTextColor} size={textSize} bold>
            {summaryHeadingLabel}
          </Text>
          <Text color={subtextColor} size={smallTextSize}>
            {summarySubtextLabel}
          </Text>
        </div>
      )
    }

    return (
      <GridWall columns={2} gutter='none'>
        <Heading size={sectionHeadingSize}>{summaryHeadingLabel}</Heading>
        {shippingOptionsStatus !== ShippingOptionStatus.Error && (
          <span className={styles.selectedShippingOption}>
            <Text size={textSize} renderAs='span'>
              {selectedShippingOptionName}
            </Text>
          </span>
        )}
      </GridWall>
    )
  }

  if (isCashTheme) {
    if (shippingOptionsStatus === ShippingOptionStatus.Error) {
      return (
        <>
          {renderSummaryHeader()}
          <ModuleContainer padding={{ vertical: 'Small' }} noBottomMargin showBorder={false} reducedHeight>
            {children}
          </ModuleContainer>
        </>
      )
    }
    return (
      <div className={themedClass.shippingOptionContainer} data-testid='shipping-option-wrapper-container'>
        <Accordion
          id={ExpandableComponent.SHIPPING_OPTION}
          onChange={handleExpandedStateChange}
          expanded={isExpanded}
          verticalAlignLabel={isCashTheme}
          disabled={isDisabled}
        >
          <Accordion.Button>{renderSummaryHeader()}</Accordion.Button>
          <Accordion.Details>{children}</Accordion.Details>
        </Accordion>
      </div>
    )
  }

  return (
    <>
      <Heading size={textSize}>{summaryHeadingLabel}</Heading>
      <div className={themedClass.shippingOptionContainer} data-testid='shipping-option-wrapper-container'>
        {children}
      </div>
    </>
  )
}
